import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useCalendarTime } from "./CalendarTimeContext";
import { useShowCancelledSessions } from "./ShowCancelledSessionContext";
import { isGroupSessionCancelledByPro } from "utils/isGroupSessionCancelled";
import { useGroupSessionsProvider } from "contexts/GroupSessionsProviderContext";

type Context = {
  groupSessions: GroupSessionMetaData[];
  show: boolean;
  setShow: (arg: boolean) => void;
  reloadGroupSessions: () => void;
};
const CalendarGroupSessionsContext = createContext<Context>({
  groupSessions: [],
  show: true,
  setShow: () => null,
  reloadGroupSessions: () => null,
});

type Props = { children: ReactNode };

export const CalendarGroupSessionsContextProvider = ({ children }: Props) => {
  const { timeRange } = useCalendarTime();
  const { showCancelledSessions } = useShowCancelledSessions();
  const { getUserGroupSessions, reloadGroupSessions } =
    useGroupSessionsProvider();
  const groupSessionsOrUndefined = getUserGroupSessions();
  const groupSessions = useMemo(
    () => groupSessionsOrUndefined || [],
    [groupSessionsOrUndefined],
  );
  const [filteredSessions, setFilteredSessions] = useState<
    GroupSessionMetaData[]
  >([]);

  const [show, setShow] = useState(true);

  // filter sessions
  useEffect(() => {
    // sanity check
    if (timeRange.length) {
      setFilteredSessions(
        groupSessions.filter((el) => {
          const isCancelled = isGroupSessionCancelledByPro(el);
          const canShow =
            (show && showCancelledSessions) ||
            (showCancelledSessions && isCancelled) ||
            (show && !isCancelled);

          if (canShow) {
            const start = +new Date(el.group_session_start);
            return timeRange[0] <= start && start <= timeRange[1];
          }

          return false;
        }),
      );
    }
  }, [timeRange, groupSessions, show, showCancelledSessions]);

  return (
    <CalendarGroupSessionsContext.Provider
      value={{
        groupSessions: filteredSessions,
        show,
        setShow,
        reloadGroupSessions,
      }}
    >
      {children}
    </CalendarGroupSessionsContext.Provider>
  );
};

export const useCalendarGroupSessions = () =>
  useContext(CalendarGroupSessionsContext);
